/**
 * 표 직렬화 (P0-③): 표 모델 ↔ BlockNote `tableContent`.
 *
 * BlockNote 형식(ground truth, lumir table-cell-props 테스트 기준):
 *   content = { type:"tableContent", columnWidths:(number|null)[], headerRows?, headerCols?,
 *               rows:[ { cells:[ { type:"tableCell", props, content:[inline] } ] } ] }
 *   cell.props = { colspan, rowspan, backgroundColor?, textColor?, textAlignment?,
 *                  verticalAlignment?, rowHeight? }   // va/rowHeight는 lumir 확장 attr
 *
 * 순수 JSON 변환(셀 content=인라인 배열; DOM 불필요). 셀 props는 **있는 그대로 보존**해
 * 라운드트립 충실도(EC3) 확보. colspan/rowspan은 그리드 구조로 왕복(normalize 재계산).
 *
 * (NOTE) BlockNote의 기본 props 포함 관례(예: backgroundColor:"default" 항상 포함 여부)는
 *   P0-⑤에서 실제 fixture로 검증·보정. 현재는 "소스에 있으면 보존, 없으면 생략" 정책.
 */

import { newMaster, newModelFromG } from "../table/model.js";

// 셀에서 그대로 왕복하는 prop 키(colspan/rowspan은 그리드로 별도 처리)
const CELL_PROP_KEYS = ["backgroundColor", "textColor", "textAlignment", "verticalAlignment", "rowHeight"];

function masterToCell(m) {
  const props = { colspan: m.colspan || 1, rowspan: m.rowspan || 1 };
  for (const k of CELL_PROP_KEYS) {
    if (m[k] !== undefined) props[k] = m[k];
  }
  return { type: "tableCell", props, content: Array.isArray(m.content) ? m.content : [] };
}

/** 표 모델 → BlockNote tableContent */
export function modelToTableContent(model) {
  const G = model.G;
  const rows = [];
  for (let r = 0; r < G.length; r++) {
    const cells = [];
    for (let c = 0; c < G[r].length; c++) {
      const m = G[r][c];
      if (!m) continue;
      if (m.r !== r || m.c !== c) continue; // master 좌상단에서만 emit(병합 슬롯 생략)
      cells.push(masterToCell(m));
    }
    rows.push({ cells });
  }
  const content = {
    type: "tableContent",
    columnWidths: (model.columnWidths || []).slice(),
    rows,
  };
  if (model.headerRows != null) content.headerRows = model.headerRows;
  if (model.headerCols != null) content.headerCols = model.headerCols;
  return content;
}

/** BlockNote tableContent → 표 모델 */
export function tableContentToModel(content) {
  const rows = (content && content.rows) || [];
  const G = [];
  const occupied = {};
  for (let r = 0; r < rows.length; r++) {
    G[r] = G[r] || [];
    let c = 0;
    const cells = rows[r].cells || [];
    for (const cell of cells) {
      while (occupied[r + "_" + c]) c++;
      const props = cell.props || {};
      const colspan = props.colspan || 1;
      const rowspan = props.rowspan || 1;
      const master = newMaster({ content: Array.isArray(cell.content) ? cell.content : [] });
      for (const k of CELL_PROP_KEYS) {
        if (props[k] !== undefined) master[k] = props[k];
      }
      for (let dr = 0; dr < rowspan; dr++) {
        for (let dc = 0; dc < colspan; dc++) {
          const rr = r + dr;
          const cc = c + dc;
          G[rr] = G[rr] || [];
          G[rr][cc] = master;
          if (!(dr === 0 && dc === 0)) occupied[rr + "_" + cc] = true;
        }
      }
      c += colspan;
    }
  }
  const model = newModelFromG(G);
  if (content && Array.isArray(content.columnWidths)) {
    // 열 수와 길이가 다르면(레거시/손상 JSON) 열 수에 맞춰 정합(초과분 절단·부족분 null 패딩). 일치 시 그대로.
    const cols = model.G[0] ? model.G[0].length : 0;
    const src = content.columnWidths;
    model.columnWidths = src.length === cols ? src.slice() : Array.from({ length: cols }, (_, i) => (i < src.length ? src[i] : null));
  }
  if (content && content.headerRows != null) model.headerRows = content.headerRows;
  if (content && content.headerCols != null) model.headerCols = content.headerCols;
  return model;
}
